import type { MutableRefObject } from 'react';
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FlexCol, PaymentLoading } from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import { Box } from '@interstate/components/Box';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as manualFieldOverridesSelectors from '@makemydeal/dr-dash-store/selectors/manualFieldOverridesSelectors';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { OFFER_TYPE } from '@makemydeal/dr-platform-types';

import { Programs } from './Programs';
import { ProgramsAndIncentivesHeader } from './ProgramsAndIncentivesHeader';
import { ManualRebatesCard } from './rebates/ManualRebatesCard';
import { RebatesTotals } from './rebates/RebatesTotals';
import { ProgramsAndIncentivesProvider } from './ProgramsAndIncentivesContext';

type ProgramsAndIncentivesCompactUIProps = {
    isIncentivesFormDirtyRef?: MutableRefObject<boolean>;
    showConfirmationModal?: boolean;
};

export const ProgramsAndIncentivesCompactUI = (
    /* istanbul ignore next */
    { isIncentivesFormDirtyRef, showConfirmationModal = false }: ProgramsAndIncentivesCompactUIProps = {}
) => {
    const dispatch = useDispatch();
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const currentCalcEndpoint = useSelector((state: any) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    const creditDecisionEnabled = useSelector(offerReduxSelectors.getCreditDecisionEnabled);
    const appliedIncentives = useSelector(offerReduxSelectors.getAppliedIncentives);
    const dealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForDealerCash);
    const nonDealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash);
    const prevCalcEndpoint = useRef(currentCalcEndpoint);
    const manualApplyRef = useRef(false);
    const isRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isExplicitLenderP1Active = useSelector(programsSelectors.isExplicitLenderP1Active);
    const dealerOptOutUpqBypassApplies = useSelector((state: any) =>
        programsSelectors.dealerOptOutUpqBypassAppliesToOffer(state, currentOfferType)
    );
    const isAspPgRedesignEnabled = useSelector(featureToggleSelectors.isAspPaymentGridRedesignEnabled);
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);
    const recordedAutoSelect = useSelector((state: any) =>
        manualFieldOverridesSelectors.getIsAutoSelectChecked(state, currentOfferType)
    );
    const appliedIncentivesRef = useRef(appliedIncentives);
    const dealerCashTotalRef = useRef(dealerCashTotal);
    const nonDealerCashTotalRef = useRef(nonDealerCashTotal);
    const isRestorationEnabledRef = useRef(isRestorationEnabled);
    const isExplicitLenderP1ActiveRef = useRef(isExplicitLenderP1Active);
    const dealerOptOutUpqBypassAppliesRef = useRef(dealerOptOutUpqBypassApplies);
    const isAspPgRedesignEnabledRef = useRef(isAspPgRedesignEnabled);
    const isImprovedAutoSelectEnabledRef = useRef(isImprovedAutoSelectEnabled);
    const recordedAutoSelectRef = useRef(recordedAutoSelect);
    appliedIncentivesRef.current = appliedIncentives;
    dealerCashTotalRef.current = dealerCashTotal;
    nonDealerCashTotalRef.current = nonDealerCashTotal;
    isRestorationEnabledRef.current = isRestorationEnabled;
    isExplicitLenderP1ActiveRef.current = isExplicitLenderP1Active;
    dealerOptOutUpqBypassAppliesRef.current = dealerOptOutUpqBypassApplies;
    isAspPgRedesignEnabledRef.current = isAspPgRedesignEnabled;
    isImprovedAutoSelectEnabledRef.current = isImprovedAutoSelectEnabled;
    recordedAutoSelectRef.current = recordedAutoSelect;
    const onProgramManualSelect = useCallback(() => {
        manualApplyRef.current = true;
    }, []);
    const shouldShowPrograms = !creditDecisionEnabled && isDealCentralExperience;

    // On unmount, restore the calculation endpoint to its previous state:
    // - If the user manually selected a program, switch to UPQ to uncheck auto-select program,
    //   unless Auto Select Program is on (DE774025).
    // - Otherwise, restore the previous endpoint since the PQ engine is only needed
    //   while working within Programs & Incentives.
    useEffect(() => {
        return () => {
            if (!isDealCentralExperience) return;

            const explicitLenderP1OwnsEndpoint = isExplicitLenderP1ActiveRef.current && currentOfferType !== OFFER_TYPE.cash;
            // A dealer already opted out of Auto Select Program has no checkbox left to
            // "uncheck" - forcing UPQ here just discards the program they manually picked.
            const dealerOptOutKeepsManualSelection = manualApplyRef.current && dealerOptOutUpqBypassAppliesRef.current;
            const skipForcedUpqRestore = explicitLenderP1OwnsEndpoint || dealerOptOutKeepsManualSelection;

            const autoSelectPinsProgram =
                (isAspPgRedesignEnabledRef.current || isImprovedAutoSelectEnabledRef.current) &&
                (recordedAutoSelectRef.current ?? prevCalcEndpoint.current === CALC_ENDPOINTS.programQuotes);

            let endpointToRestore = CALC_ENDPOINTS.userProgramQuotes;
            if (
                (!manualApplyRef.current || autoSelectPinsProgram) &&
                currentOfferType !== OFFER_TYPE.cash &&
                prevCalcEndpoint.current
            ) {
                endpointToRestore = prevCalcEndpoint.current;
            }

            const stayingOnUserProgramQuotes =
                (!skipForcedUpqRestore || !manualApplyRef.current) && endpointToRestore === CALC_ENDPOINTS.userProgramQuotes;

            if (stayingOnUserProgramQuotes && currentOfferType !== OFFER_TYPE.cash) {
                dispatch(offerReduxActionCreators.setProgramFallbackOverrides());

                if (!isRestorationEnabledRef.current && appliedIncentivesRef.current.length > 0) {
                    dispatch(
                        offerActionCreators.updateManualIncentives({
                            manualIncentives: appliedIncentivesRef.current,
                            dealerCashTotal: dealerCashTotalRef.current,
                            totalRebates: nonDealerCashTotalRef.current,
                            skipPayment: true
                        })
                    );
                }
            }

            if (skipForcedUpqRestore) return;

            dispatch(
                offerReduxActionCreators.updateCurrentCalculationEndpoint(endpointToRestore, currentOfferType, {
                    withPayment:
                        !isRestorationEnabledRef.current &&
                        manualApplyRef.current &&
                        endpointToRestore !== CALC_ENDPOINTS.programQuotes
                })
            );
        };
    }, [dispatch, isDealCentralExperience, currentOfferType]);

    return (
        <Box
            data-testid="activities-programs-container"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
        >
            <LoadingSectionParent>
                <PaymentLoading testId="trade-loading-indicator" />
                <FlexCol gap="1rem">
                    <ProgramsAndIncentivesHeader />
                    <ProgramsAndIncentivesProvider
                        showConfirmationModal={showConfirmationModal}
                        onProgramManualSelect={onProgramManualSelect}
                    >
                        <ManualRebatesCard isIncentivesFormDirtyRef={isIncentivesFormDirtyRef} />
                        {shouldShowPrograms && <Programs />}
                    </ProgramsAndIncentivesProvider>
                    <RebatesTotals />
                </FlexCol>
            </LoadingSectionParent>
        </Box>
    );
};
